import { useEffect, useState } from 'react';
import { listAttendanceEventsToday, listDisciplinaryActions, listEmployees, listLeaveRequests, listShiftSwaps, listTardiness } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  ClickableRow,
  DashboardSkeleton,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  KPICard,
  SectionCard,
  StatusBadge,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import { IconAlertTriangle, IconCalendar, IconRepeat, IconShield, IconUsers } from '../../components/icons';
import type { AttendanceEvent, DisciplinaryAction, Employee, LeaveRequest, ShiftSwap, Tardiness } from '../../types/domain';

/**
 * HR & Workforce dashboard — every KPI and panel here reads a real table
 * (employees/attendance_events/leave_requests/shift_swaps/
 * disciplinary_actions/tardiness — migrations 0035/0036/0040). A
 * genuinely-empty result shows as 0 or a small compact empty state, never
 * a fabricated number. "Terlambat" now reads the real `tardiness` table
 * (migration 0040 wires fn_record_attendance_event's own deterministic,
 * policy-based lateness computation to a permission-gated RPC) — this is
 * NOT the old fake "Terlambat 18": it is zero unless a real CLOCK_IN
 * actually exceeded a real attendance_policies.late_tolerance_minutes. A
 * 30-day attendance trend chart is still not shown — no aggregation
 * function for it exists in the backend, and fabricating one client-side
 * would be exactly the kind of thing the owner said not to do.
 */
export default function HRDashboard() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [attendanceToday, setAttendanceToday] = useState<AttendanceEvent[] | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[] | null>(null);
  const [shiftSwaps, setShiftSwaps] = useState<ShiftSwap[] | null>(null);
  const [disciplinaryActions, setDisciplinaryActions] = useState<DisciplinaryAction[] | null>(null);
  const [tardiness, setTardiness] = useState<Tardiness[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const canReadEmployee = hasPermission('employee', 'read');
  const canReadAttendance = hasPermission('attendance_event', 'read');
  const canReadLeave = hasPermission('leave_request', 'read');
  const canReadShiftSwap = hasPermission('shift_swap', 'read');
  const canReadDisciplinary = hasPermission('disciplinary_action', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadEmployee ? listEmployees(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadAttendance ? listAttendanceEventsToday(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadLeave ? listLeaveRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadShiftSwap ? listShiftSwaps(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadDisciplinary ? listDisciplinaryActions(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadAttendance ? listTardiness(activeBranchId ?? undefined) : Promise.resolve(null),
    ])
      .then(([emp, att, leave, swap, disc, tardy]) => {
        if (cancelled) return;
        setEmployees(emp);
        setAttendanceToday(att);
        setLeaveRequests(leave);
        setShiftSwaps(swap);
        setDisciplinaryActions(disc);
        setTardiness(tardy);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [canReadEmployee, canReadAttendance, canReadLeave, canReadShiftSwap, canReadDisciplinary, activeBranchId]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const hadirCount = attendanceToday ? new Set(attendanceToday.filter((a) => a.event_type === 'CLOCK_IN').map((a) => a.employee_id)).size : null;
  const pendingLeave = leaveRequests ? leaveRequests.filter((l) => l.status === 'REQUESTED') : [];
  const pendingSwap = shiftSwaps ? shiftSwaps.filter((s) => s.status === 'REQUESTED') : [];
  const activeDisciplinary = disciplinaryActions ? disciplinaryActions.filter((d) => d.status === 'ACTIVE') : [];
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const tardyToday = tardiness ? tardiness.filter((t) => new Date(t.created_at) >= startOfToday) : [];
  const employeeName = (id: string) => employees?.find((e) => e.employee_id === id)?.legal_name ?? 'Karyawan';

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        <KPICard label="Total Karyawan" value={canReadEmployee ? (employees?.length ?? 0) : '—'} hint={canReadEmployee ? undefined : 'Tanpa izin'} icon={<IconUsers size={16} />} tone="primary" />
        <KPICard label="Hadir Hari Ini" value={canReadAttendance ? (hadirCount ?? 0) : '—'} hint={canReadAttendance ? 'clock-in unik' : 'Tanpa izin'} icon={<IconShield size={16} />} tone="success" />
        <KPICard
          label="Terlambat Hari Ini"
          value={canReadAttendance ? tardyToday.length : '—'}
          hint={canReadAttendance ? 'dari kebijakan keterlambatan nyata' : 'Tanpa izin'}
          icon={<IconAlertTriangle size={16} />}
          tone={tardyToday.length > 0 ? 'danger' : 'neutral'}
        />
        <KPICard label="Cuti Pending" value={canReadLeave ? pendingLeave.length : '—'} hint={canReadLeave ? undefined : 'Tanpa izin'} icon={<IconCalendar size={16} />} tone="info" />
        <KPICard label="Tukar Shift" value={canReadShiftSwap ? pendingSwap.length : '—'} hint={canReadShiftSwap ? undefined : 'Tanpa izin'} icon={<IconRepeat size={16} />} tone="info" />
        {canReadDisciplinary && <KPICard label="Disiplin Aktif" value={activeDisciplinary.length} hint="HR/Owner only" icon={<IconAlertTriangle size={16} />} tone="danger" />}
      </div>

      <div className="alex-grid-8-4">
        {canReadEmployee && (
          <SectionCard title="Roster Karyawan">
            {!employees || employees.length === 0 ? (
              <EmptyState text="Belum ada karyawan tercatat." variant="compact" />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No. Karyawan</th>
                    <th style={thStyle}>Nama</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <ClickableRow key={emp.employee_id} onClick={() => setSelectedEmployee(emp)}>
                      <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{emp.employee_no}</td>
                      <td style={tdStyle}>{emp.legal_name}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={emp.status} />
                      </td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Cuti Pending">
            {!canReadLeave && <EmptyState text="Tidak ada izin leave_request.read." variant="compact" />}
            {canReadLeave && pendingLeave.length === 0 && <EmptyState text="Tidak ada pengajuan cuti pending." variant="compact" />}
            {canReadLeave && pendingLeave.length > 0 && (
              <table style={tableStyle}>
                <tbody>
                  {pendingLeave.slice(0, 5).map((l) => (
                    <tr key={l.leave_id}>
                      <td style={tdStyle}>{l.leave_type}</td>
                      <td style={tdStyle}>{l.date_from} – {l.date_to}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={l.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <SectionCard title="Tukar Shift">
            {!canReadShiftSwap && <EmptyState text="Tidak ada izin shift_swap.read." variant="compact" />}
            {canReadShiftSwap && pendingSwap.length === 0 && <EmptyState text="Tidak ada pengajuan tukar shift pending." variant="compact" />}
            {canReadShiftSwap && pendingSwap.length > 0 && (
              <table style={tableStyle}>
                <tbody>
                  {pendingSwap.slice(0, 5).map((s) => (
                    <tr key={s.swap_id}>
                      <td style={tdStyle}>{employeeName(s.requesting_employee_id)} → {employeeName(s.covering_employee_id)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={s.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          {canReadDisciplinary && (
            <SectionCard title="Antrean Disiplin">
              {activeDisciplinary.length === 0 ? (
                <EmptyState text="Tidak ada tindakan disiplin aktif." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {activeDisciplinary.slice(0, 5).map((d) => (
                      <tr key={d.action_id}>
                        <td style={tdStyle}>{d.action_type}</td>
                        <td style={tdStyle}>{new Date(d.issued_at).toLocaleDateString('id-ID')}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <StatusBadge status={d.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>
      </div>

      <Drawer open={!!selectedEmployee} onClose={() => setSelectedEmployee(null)} title={selectedEmployee?.legal_name ?? 'Detail Karyawan'}>
        {selectedEmployee && (
          <div>
            <DrawerField label="No. Karyawan" value={selectedEmployee.employee_no} />
            <DrawerField label="Nama" value={selectedEmployee.legal_name} />
            <DrawerField label="Status" value={<StatusBadge status={selectedEmployee.status} />} />
            <DrawerField label="Terdaftar" value={new Date(selectedEmployee.created_at).toLocaleString('id-ID')} />
            <p style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-faint)', marginTop: 12 }}>
              Data kompensasi (payroll) tidak ditampilkan di sini — memerlukan tampilan tersendiri dengan akses HR/Owner.
            </p>
          </div>
        )}
      </Drawer>
    </div>
  );
}
